import { useRef, useState, useEffect, forwardRef } from "react";
import { useNavigate } from "react-router-dom";

interface LeadFormSectionProps {
  submitted?: boolean;
  onSubmitSuccess?: () => void;
}

const LeadFormSection = forwardRef<HTMLDivElement, LeadFormSectionProps>(
  (_props, ref) => {
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    const calendlyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setVisible(true); },
        { threshold: 0.1 }
      );
      if (sectionRef.current) observer.observe(sectionRef.current);
      return () => observer.disconnect();
    }, []);

    // Load Calendly widget script and init inline widget
    useEffect(() => {
      const initWidget = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (calendlyRef.current && (window as any).Calendly) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).Calendly.initInlineWidget({
            url: "https://calendly.com/guilhermeolv1824/new-meeting?hide_gdpr_banner=1&background_color=141414&text_color=ffffff&primary_color=c9956f",
            parentElement: calendlyRef.current,
          });
        }
      };

      const existing = document.querySelector(
        'script[src="https://assets.calendly.com/assets/external/widget.js"]'
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (existing && (window as any).Calendly) {
        initWidget();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      script.onload = initWidget;
      document.body.appendChild(script);
    }, []);

    // Listen for Calendly booking event → fire Meta Pixel + navigate to Thank You
    useEffect(() => {
      const handleMessage = (e: MessageEvent) => {
        if (
          e.origin === "https://calendly.com" &&
          e.data?.event === "calendly.event_scheduled"
        ) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (typeof (window as any).fbq === "function") {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).fbq("track", "Lead");
          }
          navigate("/thank-you");
        }
      };
      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [navigate]);

    return (
      <section
        ref={sectionRef}
        className="py-24 px-6"
        style={{ background: "#0A0A0A" }}
      >
        <div
          ref={ref}
          className="max-w-3xl mx-auto"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(28px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          {/* Header */}
          <div className="text-center mb-10">
            <span
              className="inline-block text-xs font-semibold tracking-widest uppercase mb-4 px-4 py-1.5 rounded-full"
              style={{
                background: "rgba(201, 149, 111, 0.15)",
                color: "#C9956F",
                border: "1px solid rgba(201, 149, 111, 0.35)",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Yes, I Want More Clients
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ fontFamily: "'Playfair Display', serif", color: "#FFFFFF", letterSpacing: "-0.01em" }}
            >
              Claim Your Free 15-Minute Audit
            </h2>
            <p className="text-base" style={{ color: "rgba(255,255,255,0.55)", fontFamily: "'Inter', sans-serif" }}>
              Before we talk, I&apos;ll personally audit your Google profile, website, and social ads
            </p>
          </div>

          {/* Calendly Inline Widget */}
          <div
            className="rounded-lg overflow-hidden"
            style={{ border: "1px solid rgba(201, 149, 111, 0.2)" }}
          >
            <div
              ref={calendlyRef}
              style={{ minWidth: "320px", height: "700px" }}
            />
          </div>

          {/* Urgency & Scarcity */}
          <div
            className="mt-6 rounded-lg px-6 py-5 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left"
            style={{
              background: "rgba(201, 149, 111, 0.07)",
              border: "1px solid rgba(201, 149, 111, 0.25)",
            }}
          >
            <div
              className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full"
              style={{ background: "rgba(201, 149, 111, 0.15)", border: "1px solid rgba(201, 149, 111, 0.35)" }}
            >
              <i className="ri-time-line text-lg" style={{ color: "#C9956F" }} />
            </div>
            <div>
              <p
                className="text-sm font-semibold"
                style={{ color: "#FFFFFF", fontFamily: "'Inter', sans-serif" }}
              >
                Spots are limited! I only take a handful of audits each month.
              </p>
              <p
                className="text-sm mt-0.5"
                style={{ color: "rgba(255,255,255,0.5)", fontFamily: "'Inter', sans-serif" }}
              >
                NYC spots fill fast. If you&apos;re seeing this page, there may still be one open, but don&apos;t wait.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }
);

LeadFormSection.displayName = "LeadFormSection";
export default LeadFormSection;
